import { useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { faCheck, faCopy } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useThemeMode } from '../../../../../providers/theme-provider';
import {
  dilettaPanelSx,
  DILETTA_DIVIDER,
  DILETTA_TITLE,
} from '../../../../../components/v2/widgets/exotics-shared';
import { BacktesterRunRequest } from '../../../../../providers/backtesting/api/backtester-config-api.types';

interface RequestViewProps {
  // The raw JSON-RPC request last sent for this study (full or per-variant run).
  // Null until the study has been run at least once.
  request: BacktesterRunRequest | null;
}

const MONO_STACK =
  "'SFMono-Regular', 'JetBrains Mono', Menlo, Consolas, 'Liberation Mono', monospace";

// Debug/inspection view of the exact JSON-RPC request sent to the backtester
// engine — reads like an API-call spec (method + params) so the sent payload
// (e.g. options_fee) can be verified against what the engine received.
export const RequestView = ({ request }: RequestViewProps) => {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const titleColour = isDark ? DILETTA_TITLE : '#092D3A';
  const labelColour = isDark ? 'rgba(194,229,255,0.5)' : 'rgba(9,45,58,0.5)';
  const codeColour = isDark ? '#d6ecff' : '#0b2b39';
  const codeBg = isDark ? 'rgba(3, 20, 30, 0.55)' : 'rgba(9, 45, 58, 0.05)';

  const [copied, setCopied] = useState(false);

  if (!request) {
    return (
      <div className="backtest-result-view backtest-result-request">
        <Box sx={{ ...dilettaPanelSx, p: 3 }}>
          <Typography sx={{ color: titleColour, fontSize: 15, fontWeight: 700, mb: 0.5 }}>
            Request
          </Typography>
          <Typography sx={{ color: labelColour, fontSize: 13 }}>
            Run the backtest to inspect the request payload.
          </Typography>
        </Box>
      </div>
    );
  }

  const handleCopy = () => {
    // Copy the FULL request (envelope + params), not just the params block.
    navigator.clipboard
      .writeText(JSON.stringify(request, null, 2))
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard can reject on permissions — surface nothing, it's a debug aid.
      });
  };

  const paramsJson = JSON.stringify(request.params, null, 2);

  return (
    <div className="backtest-result-view backtest-result-request">
      <Box sx={{ ...dilettaPanelSx, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ color: titleColour, fontSize: 15, fontWeight: 700 }}>
              Request
            </Typography>
            <Typography sx={{ color: labelColour, fontSize: 12 }}>
              The exact JSON-RPC request last sent to the engine
            </Typography>
          </Box>
          <Tooltip title="Copy full request JSON">
            <Button
              onClick={handleCopy}
              startIcon={
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} style={{ fontSize: 12 }} />
              }
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                color: titleColour,
                borderColor: DILETTA_DIVIDER,
                px: 1.25,
                py: 0.5,
                flexShrink: 0,
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Tooltip>
        </Box>

        {/* Envelope — subtle, secondary to method/params. */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ color: labelColour, fontSize: 12, fontFamily: MONO_STACK }}>
            jsonrpc: {request.jsonrpc}
          </Typography>
          <Typography sx={{ color: labelColour, fontSize: 12, fontFamily: MONO_STACK }}>
            id: {request.id}
          </Typography>
        </Box>

        {/* Method — the star: reads like an API endpoint. */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              color: labelColour,
              fontSize: 10,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            Method
          </Typography>
          <Box
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.75,
              borderRadius: '8px',
              backgroundColor: codeBg,
              border: `1px solid ${DILETTA_DIVIDER}`,
            }}
          >
            <Typography
              component="span"
              sx={{ color: titleColour, fontSize: 15, fontWeight: 700, fontFamily: MONO_STACK }}
            >
              {request.method}
            </Typography>
          </Box>
        </Box>

        {/* Params — pretty-printed JSON in a scrollable dark code block. */}
        <Typography
          sx={{
            color: labelColour,
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          Params
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            maxHeight: 520,
            overflow: 'auto',
            borderRadius: '10px',
            backgroundColor: codeBg,
            border: `1px solid ${DILETTA_DIVIDER}`,
            color: codeColour,
            fontFamily: MONO_STACK,
            fontSize: 12.5,
            lineHeight: 1.55,
            whiteSpace: 'pre',
            tabSize: 2,
          }}
        >
          {paramsJson}
        </Box>
      </Box>
    </div>
  );
};
